import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getCatalogDrills, getCatalogRoutines } from '../auth/authApi';
import Spinner from '../components/Spinner';
import { mediaKind } from '../lib/consoleUtils';

function drillParams(d) {
  const parts = [];
  if (d.distance) parts.push(d.distance);
  if (d.mags != null) parts.push(`${d.mags} mag${d.mags === 1 ? '' : 's'}`);
  if (d.cycles != null) parts.push(`${d.cycles} cycle${d.cycles === 1 ? '' : 's'}`);
  const rounds = Array.isArray(d.rounds) ? d.rounds.reduce((a, b) => a + (Number(b) || 0), 0) : null;
  if (rounds) parts.push(`${rounds} rounds`);
  return parts.join(' · ');
}

function CatThumb({ url }) {
  const kind = mediaKind(url);
  if (!url) return <div className="thumb"><span className="noimg">No media</span></div>;
  if (kind === 'video') {
    return (
      <div className="thumb">
        <video src={url} preload="metadata" muted />
        <span className="vbadge">▶ Video</span>
      </div>
    );
  }
  return <div className="thumb"><img src={url} alt="" /></div>;
}

const ConsoleCatalog = () => {
  const { org, orgId } = useOutletContext();
  const [drills, setDrills] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('routines');
  const [selected, setSelected] = useState(null); // { kind, item }

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(''); setSelected(null);
    Promise.all([getCatalogRoutines(orgId), getCatalogDrills(orgId)])
      .then(([r, d]) => { if (alive) { setRoutines(r); setDrills(d); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [orgId]);

  const list = tab === 'routines' ? routines : drills;

  return (
    <>
      <div className="topbar">
        <div className="page-title">
          <h1>Catalog</h1>
          <p>The drills &amp; routines {org.name}’s members train with — same as the app.</p>
        </div>
        {!loading && !error && (
          <div className="seg" role="group" aria-label="Catalog type">
            <button className={tab === 'routines' ? 'on' : ''} onClick={() => setTab('routines')}>Routines ({routines.length})</button>
            <button className={tab === 'drills' ? 'on' : ''} onClick={() => setTab('drills')}>Drills ({drills.length})</button>
          </div>
        )}
      </div>

      <div className="wrap">
        {loading ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh', color: 'var(--text-muted)' }}><Spinner size={30} /></div>
        ) : error ? (
          <p className="muted">Couldn’t load the catalog. Please try again.</p>
        ) : list.length === 0 ? (
          <p className="muted">No {tab} in this org’s catalog.</p>
        ) : (
          <div className="cat-grid">
            {tab === 'routines'
              ? routines.map((r) => (
                <button key={r.id} className="cat-card" onClick={() => setSelected({ kind: 'routine', item: r })}>
                  <div className="cbody">
                    <div className="c-name">{r.name}</div>
                    <div className="c-sub">{r.shortSummary || r.skills}</div>
                    <div className="c-meta"><span className="lvl-badge">{r.level}</span><span>{(r.routineDrills || []).length} drills</span></div>
                  </div>
                </button>
              ))
              : drills.map((d) => (
                <button key={d.id} className="cat-card" onClick={() => setSelected({ kind: 'drill', item: d })}>
                  <CatThumb url={d.picture} />
                  <div className="cbody">
                    <div className="c-name">{d.name}</div>
                    <div className="c-sub">{d.shortDescription}</div>
                    <div className="c-meta"><span className="lvl-badge">{d.level}</span></div>
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>{selected.item.name}</h2>
                <p>{selected.item.level}{selected.kind === 'routine' ? ` · ${(selected.item.routineDrills || []).length} drills` : ''}</p>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {selected.kind === 'drill' ? (
                <>
                  {selected.item.picture && (
                    mediaKind(selected.item.picture) === 'video'
                      ? <video className="media" src={selected.item.picture} controls preload="metadata" playsInline />
                      : <img className="media" src={selected.item.picture} alt="" />
                  )}
                  {selected.item.description && <div className="kv"><b>Description</b>{selected.item.description}</div>}
                  {selected.item.skills && <div className="kv"><b>Skills</b>{selected.item.skills}</div>}
                  {selected.item.jacobTip && <div className="kv"><b>Coach tip</b>{selected.item.jacobTip}</div>}
                </>
              ) : (
                <>
                  {selected.item.shortSummary && <div className="kv"><b>Summary</b>{selected.item.shortSummary}</div>}
                  {selected.item.skills && <div className="kv"><b>Skills</b>{selected.item.skills}</div>}
                  <div>
                    <div className="sec-label" style={{ marginBottom: 6 }}>Drills</div>
                    {(selected.item.routineDrills || []).map((d, i) => (
                      <div className="drill-row" key={i}>
                        <span className="dr-idx">{i + 1}</span>
                        <span className="dr-name">{d.name}</span>
                        <span className="dr-params">{drillParams(d)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsoleCatalog;
